import { type OperationVariables } from '@apollo/react-common';

import { getFailureContext, getSecondaryCache, skipped, written } from './cacheBridgeCommon.ts';
import { reportCacheBridgeFailure } from './cacheBridgeDiagnostics.ts';
import { getApolloRelayCacheBridgeDisabledReason } from './cacheBridgeGate.ts';
import { getMutationResultError as getMutationResultErrorImpl } from './mutation/getMutationResultError.ts';
import { syncMutationPayload } from './mutation/syncMutationPayload.ts';
import {
	type CacheWriteResult as MutationCacheWriteResult,
	type MutationCacheBridgeSource as MutationSource,
	type MutationCacheBridgeStage,
	type MutationCacheBridgeStageArgs as MutationStageArgs,
	type MutationCacheBridgeSyncArgs as MutationSyncArgs,
	type MutationCacheBridgeWriteArgs as MutationWriteArgs,
	type MutationResultLike as MutationResult,
} from './mutation/mutationCacheBridgeTypes.ts';
import { shouldSkipCacheBridgeSync } from './utils/shouldSkipCacheBridgeSync.ts';

export type CacheWriteResult = MutationCacheWriteResult;
export type MutationCacheBridgeSource = MutationSource;
export type MutationCacheBridgeWriteArgs<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
> = MutationWriteArgs<TData, TVariables>;
export type MutationCacheBridgeStageArgs<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
> = MutationStageArgs<TData, TVariables>;
export type MutationCacheBridgeSyncArgs<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
> = MutationSyncArgs<TData, TVariables>;
export type MutationResultLike<TData> = MutationResult<TData>;

const mutationCacheBridgeStages = [syncMutationPayload];

function runMutationCacheBridgeStages<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>(
	args: MutationStageArgs<TData, TVariables>,
	stages: Array<MutationCacheBridgeStage<TData, TVariables>>,
): CacheWriteResult {
	let didWrite = false;
	let lastResult: CacheWriteResult = skipped('no-stages');

	// Mutation sync is intentionally stage-based so future work can add
	// optimistic, refetch, or connection-aware stages without changing interop
	// call sites again. Each stage must own its own safe write/error reporting.
	for (const stage of stages) {
		lastResult = stage(args);
		if (lastResult.status === 'failed') return lastResult;
		if (lastResult.status === 'written') didWrite = true;
	}

	return didWrite ? written() : lastResult;
}

export function getMutationResultError<TData>(result: MutationResultLike<TData>): unknown {
	return getMutationResultErrorImpl(result);
}

export function syncMutationCacheBridge<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>({ error, ...stageArgs }: MutationCacheBridgeSyncArgs<TData, TVariables>): CacheWriteResult {
	// Mutation mirroring only follows a successful original mutation result.
	// Optimistic payloads, partial error payloads, failed original mutations,
	// and no-cache operations intentionally do not reach any secondary sync
	// stage because the bridge only follows source-cache write behavior.
	if (error) return skipped('operation-error');

	const skipReason = shouldSkipCacheBridgeSync(stageArgs);
	if (skipReason) return skipped(skipReason);

	const disabledReason = getApolloRelayCacheBridgeDisabledReason();
	if (disabledReason) return skipped(disabledReason);

	return runMutationCacheBridgeStages(stageArgs, mutationCacheBridgeStages);
}

export function syncMutationCacheBridgeSafely<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
>(args: MutationCacheBridgeSyncArgs<TData, TVariables>): CacheWriteResult {
	try {
		return syncMutationCacheBridge(args);
	} catch (error) {
		// This catches failures before an individual stage can enter writeSafely,
		// such as experiment-gate or stage-runner errors. Secondary bridge work
		// must never turn a successful original mutation into a rejected promise.
		reportCacheBridgeFailure(
			error,
			getFailureContext('mutation', 'write', getSecondaryCache(args.source)),
		);
		return { status: 'failed', error };
	}
}
